import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { ArticleTypeOrSections } from "scmp-app/components/content/content-item-render/variants/plus-primary/helpers";
import type { plusSecondaryContentItemContent$key } from "scmp-app/queries/__generated__/plusSecondaryContentItemContent.graphql";

import {
  Container,
  ContentLink,
  Footer,
  Header,
  Headline,
  PublishedDate,
  Section,
  StyledPlusArticleTypeTitle,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  isPlus?: boolean;
  onClick?: () => void;
  reference: plusSecondaryContentItemContent$key;
  withArticleType?: boolean;
  withPublishedDate?: boolean;
  withSections?: boolean;
  withSubHeadline?: boolean;
  withSummary?: boolean;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  isPlus,
  onClick,
  ref,
  reference,
  withArticleType = true,
  withPublishedDate,
  withSections = true,
  withSubHeadline,
  withSummary,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusSecondaryContentItemContent on Content {
        __typename
        ...entityLink
        ...plusArticleTypeTitleContent
        ...helpersCheckIsPlusArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withActionBar: true
            withSections: true
            withSubHeadline: true
            withSummary: true
            withPublishedDate: true
          )
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(entity);

  // Check has section or not
  return (
    <Container className={className} ref={ref}>
      <Header>
        <ArticleTypeOrSections
          articleType={<StyledPlusArticleTypeTitle reference={entity} />}
          isPlus={isPlus}
          reference={entity}
          sections={<Section>{providers.sections({ isDisableLink: true })}</Section>}
          withArticleType={withArticleType}
          withSections={withSections}
        />
        {withPublishedDate && (
          <PublishedDate>{providers.publishedDate({ format: "d MMM yyyy" })}</PublishedDate>
        )}
        <LinkHighlightArea>
          <ContentLink onClick={onClick}>
            <Headline>
              {providers.headline({
                preferSocialHeadline: true,
              })}
            </Headline>
            {withSummary && (
              <Summary>{providers.summary({ isTextOverflowEllipsis: true })}</Summary>
            )}
            {withSubHeadline && <Summary>{providers.subHeadline()}</Summary>}
          </ContentLink>
        </LinkHighlightArea>
      </Header>
      <Footer>
        {providers.actionBar({
          displayDateVariant: "publishedDate",
          isBookmarkVisible: false,
          isCommentsVisible: false,
          isIconVisible: false,
        })}
      </Footer>
    </Container>
  );
};

Component.displayName = "ContentItemPlusSecondary";
export const ContentItemPlusSecondary = withHighlightedHeadline(Component);
